import { Reveal } from "@/lib/reveal";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sprout, HandHeart, ShieldCheck } from "lucide-react";
import { FOUNDER } from "@/data/founder";
import Signature from "@/components/Signature";
import AmbientVideo from "@/components/AmbientVideo";
import { AMBIENT_VIDEOS, AMBIENT_POSTERS } from "@/data/media";
import { usePageMeta } from "@/lib/usePageMeta";

const VALUES = [
  {
    icon: Heart,
    title: "Warmth",
    body: "Every conversation begins with care, never a script.",
  },
  {
    icon: HandHeart,
    title: "Honesty",
    body: "Real, grounded support — no toxic positivity or quick fixes.",
  },
  {
    icon: Sprout,
    title: "Patience",
    body: "We move at your pace, with room for whatever you're carrying.",
  },
  {
    icon: ShieldCheck,
    title: "Confidentiality",
    body: "What you share stays between us. Always.",
  },
];

export default function AboutPage() {
  usePageMeta({
    title: "About Maria — Calm & Connected | Lived-Experience Peer Support",
    description:
      "Meet Maria — a mother, grandmother, and caregiver who walked through adoption, single motherhood, caregiving, and loss. Calm & Connected was born from that lived experience.",
  });
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-16 md:pb-24 relative overflow-hidden">
        <span aria-hidden className="cc-orb cc-orb-1" style={{ opacity: 0.35 }} />
        <div className="cc-container grid md:grid-cols-12 gap-12 items-end relative">
          <Reveal className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              Hi, I&apos;m {FOUNDER.name}
            </p>
            <h1 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.05]">
              A real person.<br />
              A real story.<br />
              <em className="italic text-[var(--cc-primary)]">A real space for you.</em>
            </h1>
            <p className="mt-7 text-lg text-[var(--cc-text-soft)] leading-relaxed max-w-xl">
              I&apos;m a mother, a grandmother, and someone who has walked
              through many of life&apos;s harder seasons — adoption, single
              motherhood, caregiving, loss, and the long, quiet work of choosing
              healing. Calm &amp; Connected was born from that lived experience
              and a genuine desire to make sure no one has to navigate their
              journey feeling alone.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[var(--cc-line)] text-sm text-[var(--cc-text-soft)]">
                <Heart size={13} strokeWidth={1.6} className="text-[var(--cc-primary)]" />
                Mother &amp; grandmother
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[var(--cc-line)] text-sm text-[var(--cc-text-soft)]">
                <HandHeart size={13} strokeWidth={1.6} className="text-[var(--cc-primary)]" />
                Adopted &amp; caregiver
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[var(--cc-line)] text-sm text-[var(--cc-text-soft)]">
                <Sprout size={13} strokeWidth={1.6} className="text-[var(--cc-primary)]" />
                Resilient &amp; healing
              </span>
            </div>
          </Reveal>

          <Reveal delay={120} className="md:col-span-5">
            <div className="relative">
              <div className="rounded-[36px] overflow-hidden cc-card p-0 bg-[var(--cc-surface)]">
                <img
                  src={FOUNDER.portraitPrimary}
                  alt={`${FOUNDER.name}, founder of Calm & Connected`}
                  loading="eager"
                  decoding="async"
                  className="w-full h-[520px] object-contain"
                />
              </div>
              <div className="hidden md:block absolute -bottom-6 -left-6 px-5 py-3 rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.06)] border border-[var(--cc-line)]">
                <p className="font-serif text-lg leading-tight text-[var(--cc-text)]">
                  {FOUNDER.name}
                </p>
                <p className="text-xs uppercase tracking-[0.18em] text-[var(--cc-text-soft)] mt-1">
                  Founder · Calm &amp; Connected
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Calming interlude — portrait ambient video */}
      <section
        data-testid="about-video-interlude"
        className="relative overflow-hidden bg-[var(--cc-bg)]"
      >
        <div className="cc-container py-14 md:py-20 grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-5">
            <AmbientVideo
              src={AMBIENT_VIDEOS.portrait}
              poster={AMBIENT_POSTERS.portrait}
              overlay="light"
              className="w-full aspect-[3/4] cc-card"
              testId="about-ambient-video"
            />
          </Reveal>
          <Reveal delay={120} className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              In her own words
            </p>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl leading-tight">
              &ldquo;The most healing thing anyone ever did for me — they
              just <em className="italic">stayed.</em>&rdquo;
            </h2>
            <p className="mt-6 text-[var(--cc-text-soft)] leading-relaxed">
              That&apos;s what I try to offer here. Not perfect words. Not
              fixes. Just a steady, kind presence — for as long as it&apos;s
              helpful.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Personal Story */}
      <section className="cc-section bg-[var(--cc-surface)] relative overflow-hidden">
        <span aria-hidden className="cc-orb cc-orb-2" style={{ opacity: 0.3 }} />
        <div className="cc-container grid md:grid-cols-12 gap-12 items-center relative">
          <Reveal className="md:col-span-6 order-2 md:order-1">
            <div className="relative">
              <div className="rounded-[36px] overflow-hidden cc-card p-0 bg-[var(--cc-surface)]">
                <img
                  src={FOUNDER.portraitSecondary}
                  alt={`${FOUNDER.name} smiling`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[560px] object-contain"
                />
              </div>
              <div className="hidden md:block absolute -top-6 -right-6 w-28 h-28 rounded-full bg-[var(--cc-secondary)]/30 blur-2xl" />
            </div>
          </Reveal>

          <Reveal delay={100} className="md:col-span-6 order-1 md:order-2">
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              The story
            </p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
              Built from lived experience, <em className="italic">offered with heart.</em>
            </h2>
            <div className="mt-7 space-y-5 text-[var(--cc-text-soft)] leading-relaxed">
              <p>
                I&apos;ve loved deeply, lost, started over, and learned —
                slowly, gently — how much it matters to simply feel{" "}
                <em>heard</em>. Not fixed. Not advised. Heard.
              </p>
              <p>
                My life has been shaped by a lot of quiet, brave moments:
                growing up adopted and learning early what belonging really
                means; raising my children as a single mother and discovering
                strength I didn&apos;t know I had; caring for people I love
                through hard, tender seasons; sitting with loss and learning
                to keep going anyway.
              </p>
              <p>
                Through all of it, I chose healing — over and over, in small
                everyday ways. Through journaling, walks in the woods, hard
                conversations, gentler self-talk, and the slow practice of
                letting people in.
              </p>
              <p>
                Calm &amp; Connected is what I wish I&apos;d had on the
                hardest days: a steady person, no clipboard, no agenda — just
                real conversation, real warmth, and the reminder that you
                are not alone in this.
              </p>
              <p className="text-[var(--cc-text)] font-serif italic text-xl md:text-2xl pt-2">
                &ldquo;If my story can help even one person feel a little less
                alone — that&apos;s why this exists.&rdquo;
              </p>
              <div className="pt-3">
                <Signature prefix="" size="lg" testId="about-signature" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why Calm & Connected came to be — bridge between story and the work */}
      <section
        data-testid="about-origin"
        className="cc-section relative overflow-hidden"
      >
        <span aria-hidden className="cc-orb cc-orb-4" />
        <span aria-hidden className="cc-orb cc-orb-5" />
        <div className="cc-container relative">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
            <Reveal className="md:col-span-5">
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                Why this exists
              </p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                The bridge between <em className="italic">my story</em> and your space here.
              </h2>
              <p className="mt-6 text-[var(--cc-text-soft)] leading-relaxed">
                Every chapter I&apos;ve lived through — being adopted, raising
                children on my own, caring for the people I love, sitting with
                grief — taught me the same thing in different ways: the
                difference between surviving and softening is usually one
                steady person who stays.
              </p>
              <p className="mt-4 text-[var(--cc-text-soft)] leading-relaxed">
                Calm &amp; Connected is my way of being that person for someone
                else. Not as an expert. Not as a fix. As a real human who has
                walked through similar woods, and remembers how it felt to be
                handed an exhale at exactly the right moment.
              </p>
            </Reveal>

            <Reveal delay={120} className="md:col-span-7">
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  {
                    label: "From adoption",
                    body:
                      "I learned that belonging is something built — slowly, by people who choose to keep showing up.",
                    tone: "sage",
                  },
                  {
                    label: "From single motherhood",
                    body:
                      "I learned to trust my own steadiness, and to name strength even on the days it didn't feel like any.",
                    tone: "sky",
                  },
                  {
                    label: "From caregiving",
                    body:
                      "I learned how much it matters to be the calm in someone else's hard moment — and to let people be that for me, too.",
                    tone: "blush",
                  },
                  {
                    label: "From loss",
                    body:
                      "I learned that healing isn't loud. It's tender, ordinary, and chosen again and again — over walks, journaling, and quiet talks.",
                    tone: "accent",
                  },
                ].map((c, i) => {
                  const bg =
                    c.tone === "sky"
                      ? "rgba(169, 192, 204, 0.16)"
                      : c.tone === "blush"
                      ? "rgba(232, 214, 207, 0.32)"
                      : c.tone === "accent"
                      ? "rgba(217, 203, 184, 0.40)"
                      : "rgba(132, 151, 143, 0.14)";
                  return (
                    <div
                      key={c.label}
                      data-testid={`origin-card-${i}`}
                      className="cc-card p-7 h-full transition-transform duration-500 hover:-translate-y-1"
                      style={{ background: `linear-gradient(180deg, ${bg}, var(--cc-surface-2) 80%)` }}
                    >
                      <span className="text-[11px] uppercase tracking-[0.22em] text-[var(--cc-text-soft)]">
                        {c.label}
                      </span>
                      <p className="mt-4 font-serif text-xl md:text-[1.4rem] leading-snug text-[var(--cc-text)]">
                        {c.body}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 cc-card p-7 md:p-8 flex items-start gap-5"
                style={{ background: "linear-gradient(180deg, rgba(195,184,216,0.18), var(--cc-surface-2) 80%)" }}
              >
                <Heart size={20} strokeWidth={1.5} className="mt-1 text-[var(--cc-primary)] shrink-0" />
                <p className="font-serif italic text-lg md:text-xl leading-snug text-[var(--cc-text)]">
                  &ldquo;Calm &amp; Connected is what I needed on the hardest
                  days — and what I now hope to be for someone else.&rdquo;
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Moments — a glimpse of Maria's everyday life */}
      <section
        data-testid="about-moments"
        className="cc-section relative overflow-hidden"
      >
        <span aria-hidden className="cc-orb cc-orb-1" style={{ opacity: 0.25 }} />
        <div className="cc-container relative">
          <div className="max-w-2xl">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                A few moments
              </p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                The everyday corners of <em className="italic">my world.</em>
              </h2>
              <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
                Quiet walks in the woods, the in-between car moments, sunlight
                that meets you where you are. These small things are part of
                how I stay connected to myself — and to you.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid md:grid-cols-12 gap-6 md:gap-8">
            <Reveal className="md:col-span-7">
              <div className="relative rounded-[32px] overflow-hidden cc-card p-0 bg-[var(--cc-surface)]">
                <img
                  src={FOUNDER.portraitOutdoor}
                  alt={`${FOUNDER.name} on a peaceful forest walk`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[420px] md:h-[520px] object-cover"
                />
                <div className="absolute bottom-5 left-5 right-5 md:bottom-7 md:left-7 md:right-auto md:max-w-sm">
                  <div
                    className="px-5 py-4 rounded-2xl backdrop-blur-md border border-white/40"
                    style={{ background: "rgba(253, 251, 247, 0.88)" }}
                  >
                    <p className="font-serif italic text-lg md:text-xl leading-snug text-[var(--cc-text)]">
                      &ldquo;A walk in the woods always feels like an exhale.&rdquo;
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={140} className="md:col-span-5">
              <div className="relative rounded-[32px] overflow-hidden cc-card p-0 bg-[var(--cc-surface)] h-full">
                <img
                  src={FOUNDER.portraitCar}
                  alt={`${FOUNDER.name} smiling`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[420px] md:h-[520px] object-cover"
                />
                <div className="absolute top-5 right-5 px-3 py-1.5 rounded-full bg-white/90 text-xs uppercase tracking-[0.18em] text-[var(--cc-text-soft)]">
                  on the way
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission + Values */}
      <section className="cc-section relative overflow-hidden">
        <span aria-hidden className="cc-orb cc-orb-1" style={{ opacity: 0.25 }} />
        <div className="cc-container relative">
          <div className="max-w-3xl">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                The mission
              </p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                To make support feel <em className="italic">ordinary</em> again.
              </h2>
              <p className="mt-6 text-lg text-[var(--cc-text-soft)] leading-relaxed">
                Approachable. Judgment-free. Grounded in the simple act of
                being heard. No labels, no diagnoses, no expectations — just a
                steady person who shows up for you.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid md:grid-cols-4 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 100}>
                  <div className="cc-card p-7 h-full transition-transform duration-500 hover:-translate-y-1">
                    <div className="w-11 h-11 rounded-full bg-[var(--cc-surface)] border border-[var(--cc-line)] flex items-center justify-center">
                      <Icon size={17} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
                    </div>
                    <h3 className="mt-5 font-serif text-2xl">{v.title}</h3>
                    <p className="mt-2 text-[var(--cc-text-soft)] leading-relaxed">
                      {v.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Personal CTA */}
      <section className="cc-section bg-[var(--cc-surface)]">
        <div className="cc-container text-center max-w-2xl mx-auto">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              When you&apos;re ready, I&apos;m here.
            </h2>
            <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
              There&apos;s no &ldquo;right&rdquo; time. No perfect words.
              Whenever and however you want to start, the door is open.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/booking" data-testid="about-cta-primary" className="cc-btn-primary">
                Schedule a Conversation <ArrowRight size={16} strokeWidth={1.7} />
              </Link>
              <Link to="/services" data-testid="about-cta-secondary" className="cc-btn-secondary">
                See Support Options
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
